import { z } from 'zod';
import { COLLAB_SUBJECT_PAGES, COLLAB_SUBJECTS, collabSubjectPath } from './collab';
import type { ModuleKey } from './modules';

/** Busca global (T-203, RF-43, PRD §11.28; DEC-069): os tipos, na ordem dos grupos. */
export const SEARCH_TYPES = ['objective', 'kpi', 'initiative', 'meeting', 'document'] as const;
export type SearchTypeName = (typeof SEARCH_TYPES)[number];
export const SEARCH_TYPE_LABELS: Record<SearchTypeName, string> = {
  objective: 'Objetivos',
  kpi: 'KPIs',
  initiative: 'Iniciativas',
  meeting: 'Reuniões',
  document: 'Documentos',
};

/** Até quantos resultados por grupo (o total vem junto). */
export const SEARCH_GROUP_LIMIT = 8;
export const SEARCH_MIN_LENGTH = 2;

/**
 * Palavras da busca como o banco compara: sem acento, em minúsculas, separadas por qualquer coisa que não seja letra
 * ou número; até 6 palavras, sem repetir, com pelo menos 2 letras.
 */
export function searchTokens(q: string): string[] {
  const words = q
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((w) => w.length >= SEARCH_MIN_LENGTH);
  return [...new Set(words)].slice(0, 6);
}

export const searchQuerySchema = z.object({
  q: z
    .string()
    .trim()
    .max(100, 'Busca longa demais.')
    .refine((q) => searchTokens(q).length > 0, 'Digite pelo menos 2 letras.'),
  projectId: z.string().uuid(),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;

export const searchItemSchema = z.object({
  type: z.enum(SEARCH_TYPES),
  id: z.string().uuid(),
  title: z.string(),
  /** Onde está: perspectiva, código, objetivo, data da reunião ou o assunto do documento. */
  subtitle: z.string().nullable(),
  /** Documento: o assunto a que o arquivo ou link está anexado (para abrir na tela dele). */
  subject: z
    .object({
      type: z.enum(COLLAB_SUBJECTS),
      id: z.string().uuid(),
      /** Ação: o plano dela. */
      planId: z.string().uuid().nullable(),
    })
    .nullable(),
});
export type SearchItem = z.infer<typeof searchItemSchema>;

export const searchResultSchema = z.object({
  groups: z.array(
    z.object({
      type: z.enum(SEARCH_TYPES),
      label: z.string(),
      total: z.number().int(),
      items: z.array(searchItemSchema),
    }),
  ),
});
export type SearchResult = z.infer<typeof searchResultSchema>;

/** Telas onde a busca procura (objetivos, KPIs, iniciativas, reuniões e os assuntos dos documentos). */
export const SEARCH_MODULES = [
  'objetivos',
  'indicadores',
  'iniciativas',
  'reunioes-decisoes',
  'planos-acao',
  'diagnostico',
] as const satisfies readonly ModuleKey[];

/**
 * Tela que o resultado abre, já com o projeto e o registro: a mesma dos links das notificações. KPI vai ao histórico
 * dele; sem acesso ao histórico, à lista de KPIs filtrada pelo nome. Documento abre o assunto a que está anexado.
 */
export function searchItemPath(
  organizationId: string,
  projectId: string,
  item: SearchItem,
  can: (module: ModuleKey) => boolean,
): string | null {
  const at = (type: keyof typeof COLLAB_SUBJECT_PAGES, id: string, planId: string | null = null) =>
    collabSubjectPath({ organizationId, projectId, type, id, planId });
  switch (item.type) {
    case 'objective':
      return at('objective', item.id);
    case 'kpi':
      if (can(COLLAB_SUBJECT_PAGES.kpi.module)) return at('kpi', item.id);
      return `/empresas/${organizationId}/indicadores/kpis?${new URLSearchParams({ projeto: projectId, q: item.title }).toString()}`;
    case 'initiative':
      return at('initiative', item.id);
    case 'meeting':
      return at('meeting', item.id);
    case 'document':
      return item.subject ? at(item.subject.type, item.subject.id, item.subject.planId) : null;
  }
}
